/**
 * Validation Manager
 *
 * Gestisce tutte le validazioni della sidebar e le condizioni per procedere al checkout
 * Pattern funzionale puro: input => output, nessuna mutazione di stato
 */

import { larger } from 'mathjs'

/**
 * Verifica se tutte le condizioni esterne sono validate
 *
 * @param {import('../types').ExternalCondition[]} external_conditions - Array di condizioni esterne
 * @returns {boolean} - true se tutte le condizioni hanno sidebar_validation = true
 */
export function validate_external_conditions(external_conditions) {
  if (!external_conditions || external_conditions.length === 0) return true

  return external_conditions.every(condition => condition.sidebar_validation === true)
}

/**
 * Determina se la sidebar deve essere bloccata visualmente
 *
 * La sidebar è bloccata se:
 * - Esistono condizioni esterne non validate
 * - Almeno una di queste ha il flag sidebar_lock = true
 *
 * @param {import('../types').ExternalCondition[]} external_conditions - Array di condizioni esterne
 * @returns {boolean} - true se la sidebar deve essere bloccata (applica opacità e disabilita pointer events)
 */
export function should_lock_sidebar(external_conditions) {
  // Se tutte le condizioni sono validate => no lock
  if (validate_external_conditions(external_conditions)) return false

  // Verifica se c'è almeno una condizione esterna bloccante
  const blocking_conditions = external_conditions.filter(c => c.sidebar_lock === true)

  return blocking_conditions.length > 0
}

/**
 * Verifica se il checkout può procedere (abilita il pulsante "Prenota")
 *
 * Validazione completa che verifica:
 * - Inizializzazione sidebar_blocks
 * - Condizioni esterne validate
 * - Blocchi richiesti validati (bill_to_pax, booker, guest)
 * - OTP validato (se richiesto)
 * - Stripe completato (se necessario pagamento carta)
 * - Somma pagamenti >= live_price
 *
 * @param {import('../types').CheckoutValidationContext} context - Contesto di validazione
 * @returns {boolean} - true se il checkout può procedere, false altrimenti
 */
export function can_proceed_to_checkout(context) {
  const {
    sidebar_blocks,
    external_conditions,
    bill_to_pax_required,
    booker_required,
    otp_required,
    otp_check,
    credit_card_amount,
    stripe_completed,
    is_availability_known,
    live_price,
    payments_sum,
  } = context

  // Verifica che sidebar_blocks sia inizializzato
  if (!sidebar_blocks || Object.keys(sidebar_blocks).length === 0) {
    return false
  }

  // Verifica condizioni esterne
  if (!validate_external_conditions(external_conditions)) {
    return false
  }

  // Verifica bill_to_pax se richiesto
  if (bill_to_pax_required) {
    if (sidebar_blocks.bill_to_pax && sidebar_blocks.bill_to_pax.payload === false) {
      return false
    }
  }

  // Verifica booker se richiesto
  if (booker_required) {
    if (sidebar_blocks.booker && sidebar_blocks.booker.payload === false) {
      return false
    }
  }

  // Verifica dati guest (sempre obbligatorio)
  if (sidebar_blocks.guest && sidebar_blocks.guest.payload === false) {
    return false
  }

  // Verifica OTP se richiesto
  if (otp_required) {
    if (otp_check !== 'VALID' && otp_check !== 'ALREADY_VALIDATED') {
      return false
    }
  }

  // Verifica Stripe se necessario pagamento con carta
  if (credit_card_amount > 0 && !stripe_completed) {
    return false
  }

  // Verifica che la somma dei pagamenti copra il live_price
  // TODO: gestire eccezione Jointly (prenotare anche con credito inferiore)
  if (is_availability_known && larger(live_price, payments_sum)) {
    return false
  }

  return true
}